import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/tipoambiente.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const TipoMaterialTetoManager = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    descricao: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [camposComErro, setCamposComErro] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/tipo-material-teto');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      if (error.response && error.response.status === 403) {
        mostrarMensagem('error', 'Você não tem permissão para acessar este recurso.');
      } else {
        mostrarMensagem('error', `Erro de conexão: ${error.message}`);
      }
    }
  }, [mostrarMensagem]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/tipo-material-teto');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
  }, [verificarAPI]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'tipo de teto', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/tipo-material-teto/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'tipo de teto', response.data);
      mostrarMensagem('success', 'Registro atualizado com sucesso!');
      carregarDados(false);
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'tipo de teto');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem, carregarDados]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'tipo de teto', { id });
    try {
      const response = await api.delete(`/tipo-material-teto/${id}`);
      logarSucesso('Excluir', 'tipo de teto', response.data);
      mostrarMensagem('success', 'Registro excluído com sucesso!');
      carregarDados(false);
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'tipo de teto');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem, carregarDados]);

  const colunas = useMemo(() => [
    {
      title: 'Salvar',
      field: 'salvar',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        atualizarRegistro(data.id, { descricao: data.descricao });
      },
      headerSort: false
    },
    {
      title: 'Excluir',
      field: 'excluir',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
      cellClick: (e, cell) => {
        if (window.confirm('Tem certeza que deseja excluir este registro?')) {
          const data = cell.getRow().getData();
          excluirRegistro(data.id);
        }
      },
      headerSort: false
    },
    {
      title: 'ID',
      field: 'id',
      width: 80,
      headerFilter: 'input',
      sorter: 'number'
    },
    {
      title: 'Descrição',
      field: 'descricao',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar Descrição...',
      sorter: 'string',
      editor: 'input'
    }
  ], [atualizarRegistro, excluirRegistro]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
      }
      .btn-delete:hover {
        background: #c82333;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    inicializarTabulator();
  }, [inicializarTabulator]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();

    const { descricao } = formData;

    // Validação
    const camposObrigatorios = [];
    const erros = [];

    if (!descricao || descricao.trim() === '') {
      camposObrigatorios.push('Descrição');
      erros.push('descricao');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = { descricao };

    logarRequisicao('Adicionar', 'tipo de teto', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/tipo-material-teto', dataToSend);
      logarSucesso('Adicionar', 'tipo de teto', response.data);
      setFormData({ descricao: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
      await carregarDados(false);
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'tipo de teto');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (value) {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'tipo_teto.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Tipos de Teto</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_tipo_teto</p>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="descricao">Descrição:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="descricao"
              name="descricao"
              value={formData.descricao}
              onChange={handleInputChange}
              placeholder="Ex: Laje de concreto"
              maxLength={255}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('descricao') ? 'input-error' : ''}
            />
          </div>
          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>
            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span><strong>Campos obrigatórios:</strong> Descrição</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default TipoMaterialTetoManager;
